"use client";

import { useActionState, useEffect, useRef, type ComponentProps, type ReactNode } from "react";
import { useFormStatus } from "react-dom";
import { useRouter } from "@frontend/lib/navigation";
import type { ActionState } from "@frontend/lib/actions";
import { Button, Callout, cx } from "./ui";

/**
 * Wraps a server action with inline pending and error handling, so every action
 * in the collab room reports failures in place rather than throwing the user out
 * to an error page. Domain errors here are usually recoverable — someone else
 * acted first, or a countdown lapsed mid-click.
 */
export function ActionForm({
  action,
  children,
  className,
  successMessage,
  footer,
}: {
  action: (state: ActionState, formData: FormData) => Promise<ActionState>;
  children: ReactNode;
  className?: string;
  successMessage?: string;
  /**
   * Submit controls for a form long enough that a button floating after the last
   * field reads as unrelated to it. Given one, outcomes report inside the bar
   * next to the button that caused them instead of as a banner below the fields.
   */
  footer?: ReactNode;
}) {
  const router = useRouter();
  const [state, formAction] = useActionState<ActionState, FormData>(action, {});
  const handled = useRef<ActionState | null>(null);

  useEffect(() => {
    if (state === handled.current) return;
    handled.current = state;
    if (state.ok) router.refresh();
  }, [state, router]);

  if (footer) {
    return (
      <form action={formAction} noValidate className={className}>
        <div className="space-y-3">{children}</div>
        <div className="-mx-5 -mb-4 mt-5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-line/80 bg-canvas/40 px-5 py-3">
          <p aria-live="polite" className="min-w-0 text-xs">
            {state.error ? (
              <span className="text-warn-700">{state.error}</span>
            ) : state.ok && successMessage ? (
              <span className="text-money-700">{successMessage}</span>
            ) : (
              <span className="text-ink-subtle">Changes apply as soon as you save.</span>
            )}
          </p>
          <div className="flex shrink-0 items-center gap-2">{footer}</div>
        </div>
      </form>
    );
  }

  return (
    <form action={formAction} noValidate className={cx("space-y-3", className)}>
      {children}
      {state.error ? <Callout tone="amber">{state.error}</Callout> : null}
      {state.ok && successMessage ? <Callout tone="green">{successMessage}</Callout> : null}
    </form>
  );
}

export function SubmitButton({
  children,
  pendingLabel,
  disabled,
  ...props
}: ComponentProps<typeof Button> & { pendingLabel?: string }) {
  const { pending } = useFormStatus();
  return (
    <Button type="submit" disabled={pending || disabled} {...props}>
      {pending ? (pendingLabel ?? "Working…") : children}
    </Button>
  );
}
